// frontend/src/lib/actions/swipe.ts
//
// Two Svelte actions:
//   horizontalSwipe — observe horizontal pointer drags on an element. Calls
//     callbacks with translateX during the drag and on release (left or right).
//   edgeSwipe — observe pointer drags that begin within `edge` px of the
//     viewport's left or right edge. Used to open a side drawer.

export interface HorizontalSwipeOptions {
	threshold?: number; // px the user must drag for it to commit (default 60)
	maxVerticalDrift?: number; // cancel if vertical drift exceeds this (default 24)
	onMove?: (dx: number) => void;
	onCommit?: (direction: 'left' | 'right') => void;
	onCancel?: () => void;
}

export function horizontalSwipe(node: HTMLElement, options: HorizontalSwipeOptions = {}) {
	let startX = 0;
	let startY = 0;
	let active = false;
	let pointerId: number | null = null;

	const cfg = () => ({
		threshold: options.threshold ?? 60,
		maxVerticalDrift: options.maxVerticalDrift ?? 24,
		onMove: options.onMove,
		onCommit: options.onCommit,
		onCancel: options.onCancel,
	});

	function down(e: PointerEvent) {
		if (e.pointerType === 'mouse') return; // touch + pen only
		startX = e.clientX;
		startY = e.clientY;
		active = true;
		pointerId = e.pointerId;
	}

	function move(e: PointerEvent) {
		if (!active || e.pointerId !== pointerId) return;
		const dx = e.clientX - startX;
		const dy = e.clientY - startY;
		if (Math.abs(dy) > cfg().maxVerticalDrift) {
			cancel();
			return;
		}
		cfg().onMove?.(dx);
	}

	function up(e: PointerEvent) {
		if (!active || e.pointerId !== pointerId) return;
		const dx = e.clientX - startX;
		const t = cfg().threshold;
		if (Math.abs(dx) >= t) {
			cfg().onCommit?.(dx < 0 ? 'left' : 'right');
		} else {
			cfg().onCancel?.();
		}
		active = false;
		pointerId = null;
	}

	function cancel() {
		if (!active) return;
		cfg().onCancel?.();
		active = false;
		pointerId = null;
	}

	node.addEventListener('pointerdown', down);
	node.addEventListener('pointermove', move);
	node.addEventListener('pointerup', up);
	node.addEventListener('pointercancel', cancel);

	return {
		update(next: HorizontalSwipeOptions) {
			options = next;
		},
		destroy() {
			node.removeEventListener('pointerdown', down);
			node.removeEventListener('pointermove', move);
			node.removeEventListener('pointerup', up);
			node.removeEventListener('pointercancel', cancel);
		},
	};
}

export interface EdgeSwipeOptions {
	edge?: 'left' | 'right';
	edgeWidth?: number; // px from edge where the swipe must originate
	threshold?: number; // px the user must drag inward for commit
	onCommit?: () => void;
}

export function edgeSwipe(node: HTMLElement | Window, options: EdgeSwipeOptions = {}) {
	let startX = 0;
	let startY = 0;
	let active = false;
	let pointerId: number | null = null;

	const cfg = () => ({
		edge: options.edge ?? 'left',
		edgeWidth: options.edgeWidth ?? 20,
		threshold: options.threshold ?? 60,
		onCommit: options.onCommit,
	});

	function down(e: PointerEvent) {
		if (e.pointerType === 'mouse') return;
		const c = cfg();
		const w = window.innerWidth;
		const fromLeft = e.clientX <= c.edgeWidth;
		const fromRight = e.clientX >= w - c.edgeWidth;
		if (c.edge === 'left' && !fromLeft) return;
		if (c.edge === 'right' && !fromRight) return;
		startX = e.clientX;
		startY = e.clientY;
		active = true;
		pointerId = e.pointerId;
	}

	function up(e: PointerEvent) {
		if (!active || e.pointerId !== pointerId) return;
		const dx = e.clientX - startX;
		const dy = e.clientY - startY;
		const c = cfg();
		if (Math.abs(dy) < 30) {
			if (c.edge === 'left' && dx >= c.threshold) c.onCommit?.();
			if (c.edge === 'right' && -dx >= c.threshold) c.onCommit?.();
		}
		active = false;
		pointerId = null;
	}

	// PointerEvent listeners: Window doesn't have addEventListener typed for pointerdown
	// in older lib.dom; cast to any.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const target: any = node;
	target.addEventListener('pointerdown', down);
	target.addEventListener('pointerup', up);

	return {
		update(next: EdgeSwipeOptions) {
			options = next;
		},
		destroy() {
			target.removeEventListener('pointerdown', down);
			target.removeEventListener('pointerup', up);
		},
	};
}
